import React, { useState, useEffect, useCallback } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faAngleUp } from "@fortawesome/free-solid-svg-icons";

import AdminNavBar from "../components/AdminNavBar";
import "bootstrap/dist/css/bootstrap.min.css";
import "react-bootstrap";
import "bootstrap/dist/js/bootstrap.bundle.min.js";
import Toast from "../components/Toast";
import {
  AdminLoadingState,
  AdminPageBody,
  AdminSectionHeader,
  AdminTablePanel,
  adminPrimaryButton,
} from "../components/AdminPageShell";
import { useAdminResourcePersons } from "../store/admin";

import AddResourcePersonModal from "./components/resourcePersons/AddResourcePersonModal";
import DisableUserModal from "./components/resourcePersons/DisableUserModal";
import UpdateResourcePersonModal from "./components/resourcePersons/UpdateResourcePersonModal";
import ViewResourcePersonModal from "./components/resourcePersons/ViewResourcePersonModal";
import ResourcePersonTable from "./components/resourcePersons/ResourcePersonTable";
import ResourcePersonFilters from "./components/resourcePersons/ResourcePersonFilters";
import ResourcePersonPagination from "./components/resourcePersons/ResourcePersonPagination";

const EMPTY_ADD_FORM = {
  name: "",
  email: "",
  phone: "",
  bio: "",
};

// ===== RESOURCE PERSON MANAGEMENT (uses /api/admin/resource-persons) =====
const ResourcePersonManagement = () => {
  const {
    resourcePersons,
    resourcePersonsLoading,
    resourcePersonsTotal,
    resourcePersonsPages,
    fetchResourcePersons,
    createResourcePerson,
    updateResourcePersonStatus,
    resourcePersonMutationLoading,
  } = useAdminResourcePersons();

  const [isLoading, setIsLoading] = useState(true);
  const [isReloading, setIsReloading] = useState(false);
  const [page, setPage] = useState(1);
  const [sort, setSort] = useState("newest");
  const [status, setStatus] = useState("");
  const [search, setSearch] = useState("");
  const [reload, setReload] = useState(false);

  const [isSuccess, setIsSuccess] = useState(true);
  const [toast, setToast] = useState(null);
  const showToast = useCallback((message, success = true) => {
    setToast(message);
    setIsSuccess(success);
    setTimeout(() => setToast(null), 5000);
  }, []);

  const [selectedUser, setSelectedUser] = useState(null);

  // GET /api/admin/resource-persons?page&limit&search&status&sort
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setIsReloading(true);
      const result = await fetchResourcePersons({
        page,
        limit: 10,
        search,
        status,
        sort,
      });
      if (cancelled) return;
      if (!result.success) {
        showToast(result.error || "Failed to fetch resource persons", false);
      }
      setIsLoading(false);
      setIsReloading(false);
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [fetchResourcePersons, page, sort, status, search, reload, showToast]);

  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isDisableModalOpen, setIsDisableModalOpen] = useState(false);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [isUpdateModalOpen, setIsUpdateModalOpen] = useState(false);

  // PATCH /api/admin/resource-persons/:id/status — STATUS action (modal)
  const handleDisableUser = async () => {
    if (!selectedUser) return;
    const nextStatus = selectedUser.status === "ACTIVE" ? "DEACTIVATED" : "ACTIVE";
    const result = await updateResourcePersonStatus(selectedUser.id, nextStatus);
    if (!result.success) {
      showToast(result.error || "Request failed", false);
    } else {
      showToast("Success!");
      setIsDisableModalOpen(false);
      setReload(!reload);
    }
  };

  // VIEW action — opens the details modal using the row ID.
  const handleViewUser = (user) => {
    setSelectedUser(user);
    setIsViewModalOpen(true);
  };

  // UPDATE action — opens the PATCH modal.
  const handleUpdateUser = (user) => {
    setSelectedUser(user);
    setIsUpdateModalOpen(true);
  };

  // Called after a successful PATCH /api/admin/resource-persons/:id.
  const handleUpdateSuccess = () => {
    showToast("Resource Person updated successfully");
    setIsUpdateModalOpen(false);
    setReload(!reload);
  };

  const [addFormData, setAddFormData] = useState(EMPTY_ADD_FORM);
  const [addError, setAddError] = useState("");

  const handleAddChange = (e) => {
    const { name, value } = e.target;
    setAddFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  // POST /api/admin/resource-persons — { name, email, phone, bio }
  const handleAddSubmit = async (e) => {
    e.preventDefault();
    const result = await createResourcePerson(addFormData);
    if (!result.success) {
      setAddError(result.error || "Failed to add resource person");
    } else {
      showToast("Resource Person Added successfully");
      setIsAddModalOpen(false);
      setAddError("");
      setAddFormData(EMPTY_ADD_FORM);
      setReload(!reload);
    }
  };

  const handleSearchSubmit = (value) => {
    setPage(1);
    setSearch(value);
  };

  const handleSortChange = (value) => {
    setPage(1);
    setSort(value);
  };

  const handleStatusChange = (value) => {
    setPage(1);
    setStatus(value);
  };

  const handleToggleStatus = (user) => {
    setSelectedUser(user);
    setIsDisableModalOpen(true);
  };

  return (
    <div>
      <AdminNavBar
        title="Resource Person"
        context="Add and Manage all resource person accounts here."
      />
      <AdminPageBody>
        {toast && <Toast message={toast} onClose={() => setToast(null)} isSuccess={isSuccess} />}
        {isLoading ? (
          <AdminLoadingState />
        ) : (
          <div className="sub-body space-y-6">
            <button
              className="fixed bottom-6 right-6 z-30 flex h-11 w-11 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-700 shadow-lg transition hover:bg-slate-50 md:right-8"
              onClick={() => {
                const subBody = document.querySelector(".sub-body");
                if (subBody) {
                  subBody.scrollTo({ top: 0, behavior: "smooth" });
                }
              }}
            >
              <FontAwesomeIcon icon={faAngleUp} />
            </button>
            <AdminSectionHeader
              title="All Resource Persons"
              description="Manage all resource person accounts from one structured workspace."
              count={`${resourcePersonsTotal} resource persons`}
              actions={
                <button
                  type="button"
                  onClick={() => {
                    setIsAddModalOpen(true);
                  }}
                  className={adminPrimaryButton}
                >
                  Add Resource Person
                </button>
              }
            />

            <ResourcePersonFilters
              search={search}
              sort={sort}
              status={status}
              onSearchSubmit={handleSearchSubmit}
              onSortChange={handleSortChange}
              onStatusChange={handleStatusChange}
            />

            <AdminTablePanel>
              <ResourcePersonTable
                users={resourcePersons}
                isReloading={isReloading || resourcePersonsLoading}
                onToggleStatus={handleToggleStatus}
                onView={handleViewUser}
                onUpdate={handleUpdateUser}
              />
            </AdminTablePanel>
            {!isReloading && resourcePersons.length !== 0 && (
              <ResourcePersonPagination
                page={page}
                lastPage={resourcePersonsPages}
                onPageChange={setPage}
              />
            )}
          </div>
        )}
      </AdminPageBody>

      <AddResourcePersonModal
        isOpen={isAddModalOpen}
        onClose={() => {
          setIsAddModalOpen(false);
          setAddError("");
        }}
        onSubmit={handleAddSubmit}
        isLoading={resourcePersonMutationLoading}
        error={addError}
        formData={addFormData}
        onFormChange={handleAddChange}
      />

      <DisableUserModal
        isOpen={isDisableModalOpen}
        user={selectedUser}
        onClose={() => setIsDisableModalOpen(false)}
        onConfirm={handleDisableUser}
        isLoading={resourcePersonMutationLoading}
      />

      <ViewResourcePersonModal
        isOpen={isViewModalOpen}
        user={selectedUser}
        onClose={() => setIsViewModalOpen(false)}
      />

      <UpdateResourcePersonModal
        isOpen={isUpdateModalOpen}
        user={selectedUser}
        onClose={() => setIsUpdateModalOpen(false)}
        onSuccess={handleUpdateSuccess}
      />
    </div>
  );
};

export default ResourcePersonManagement;
